import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map, catchError, timeout, retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private apiImagePath = environment.apiImagePath;

  constructor(private http: HttpClient) {}

  getImagePath(relativeUrl: string): string {
    return this.apiImagePath + relativeUrl;
  }

  getArticleImagePath(relativeUrl: string) {
    const url = this.apiImagePath + relativeUrl;
    return url.replace(/\/$/, '');
  }

  handleImageUrl(url: string): string {
    if (!url) return '';
    return url.replace(/([^:]\/)\/+/g, '$1');
  }

  updateImageUrl(path: string): string {
    const cleanedPath = path.replace(/\\/g, '/');
    return cleanedPath;
  }

  // ============================================
  // MAIN FIX: Smart image loading with fallback
  // ============================================
  getSecureImage(imageUrl: string): Observable<string> {
    // Check if this is a static asset path
    const isStaticAsset = this.isStaticAssetPath(imageUrl);

    if (isStaticAsset) {
      return this.loadImageWithoutAuth(imageUrl);
    }

    // For dynamic/protected images, try with authentication first
    const token = localStorage.getItem('accessToken');

    if (!token) {
      return this.loadImageWithoutAuth(imageUrl);
    }

    // Try with authentication
    return this.loadImageWithAuth(imageUrl, token).pipe(
      catchError((error: HttpErrorResponse) => {
        console.warn('Auth request failed, trying without auth:', error.status);

        // If authentication fails (500, 401, 403), try without auth
        if ([500, 401, 403].includes(error.status)) {
          return this.loadImageWithoutAuth(imageUrl);
        }

        // For other errors, return placeholder
        console.error('Image loading failed completely');
        return of('assets/img/placeholder.png');
      })
    );
  }

  // ============================================
  // Check if path is a static asset
  // ============================================
  private isStaticAssetPath(url: string): boolean {
    const staticPaths = [
      '/assets/cvs/',
      '/assets/cls/',
      '/assets/img/',
      '/assets/ImagesandVideos/',
      'assets/cvs/',
      'assets/cls/',
      'assets/img/',
      'assets/ImagesandVideos/',
    ];

    return staticPaths.some((path) => url.includes(path));
  }

  // ============================================
  // Load image WITH authentication
  // ============================================
  private loadImageWithAuth(
    imageUrl: string,
    token: string
  ): Observable<string> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .get(imageUrl, {
        headers,
        responseType: 'blob',
      })
      .pipe(
        timeout(10000), // 10 second timeout
        map((blob) => {
          // console.log('Image loaded WITH auth, size:', blob.size);
          return URL.createObjectURL(blob);
        })
      );
  }

  // ============================================
  // Load image WITHOUT authentication (fallback)
  // ============================================
  private loadImageWithoutAuth(imageUrl: string): Observable<string> {
    return this.http
      .get(imageUrl, {
        responseType: 'blob',
      })
      .pipe(
        timeout(10000),
        map((blob) => {
          return URL.createObjectURL(blob);
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Failed to load image without auth:', error);

          // If it's 404, the file doesn't exist
          if (error.status === 404) {
            console.error('Image not found (404):', imageUrl);
          }

          // Return placeholder or original URL
          return of('assets/img/placeholder.png');
        })
      );
  }

  // ============================================
  // BONUS: Download file with proper authentication handling
  // ============================================
  downloadSecureFile(fileUrl: string, fileName: string): Observable<Blob> {
    const token = localStorage.getItem('accessToken');

    const headers = token
      ? new HttpHeaders({
          Authorization: `Bearer ${token}`,
        })
      : new HttpHeaders();

    return this.http
      .get(fileUrl, {
        headers,
        responseType: 'blob',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(
            ' Download failed with auth, trying without:',
            error.status
          );

          if ([500, 401, 403].includes(error.status)) {
            // Retry without auth
            return this.http.get(fileUrl, { responseType: 'blob' });
          }

          return throwError(() => error);
        })
      );
  }
}
